import { useState } from "react";
import { UserRoundSearch } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/auth-context";
import { useImpersonationTargets, useStartImpersonation } from "@/hooks/security/use-impersonation";

/**
 * Ver a app pelos olhos de um colaborador. Só para superadmins.
 *
 * Serve para responder a "porque é que ela não vê este menu?" sem pedir a
 * palavra-passe a ninguém. A sessão que daí resulta é só-leitura.
 */
export const ImpersonationPicker = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data: targets = [], isLoading } = useImpersonationTargets(search, open);
  const start = useStartImpersonation();

  // Já a personificar? O botão não aparece — quem sai é a barra de aviso.
  if (!user?.isSuperuser || user.impersonated_by) return null;

  const escolher = async (id: string) => {
    try {
      await start.mutateAsync(id);
    } catch {
      toast.error("Não foi possível ver a app como esse colaborador");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Ver a app como outro colaborador">
              <UserRoundSearch className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Ver como outro colaborador</TooltipContent>
      </Tooltip>

      <PopoverContent className="w-80 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Pesquisar colaborador..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-72">
            {isLoading ? (
              <p className="p-3 text-sm text-muted-foreground">A pesquisar...</p>
            ) : (
              <CommandEmpty className="p-3 text-sm text-muted-foreground">
                Nenhum colaborador encontrado.
              </CommandEmpty>
            )}

            {targets.length > 0 && (
              <CommandGroup heading="Ver a app como">
                {targets.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={t.id}
                    onSelect={() => escolher(t.id)}
                    disabled={start.isPending}
                    className="flex flex-col items-start gap-0.5"
                  >
                    <span className="font-medium">{t.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {[t.employee_number, t.email].filter(Boolean).join(" · ")}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
